<template>
  <el-dialog
    v-model="visible"
    title="入库通知设置"
    width="760px"
    destroy-on-close
    @open="onOpen"
  >
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="tip"
      title="说明"
      description="按人勾选要接收的入库类型：采购/加工均区分「入我方仓库（回仓）」与「中转直发厂」。未勾选任何类型的人不接收通知。报损出库不纳入通知。"
    />

    <div class="section">
      <div class="section-head">
        <div class="section-title">接收人与通知类型</div>
        <div class="toolbar">
          <el-input
            v-model="filterKeyword"
            placeholder="筛选姓名 / 用户名 / 部门"
            clearable
            style="width: 240px"
          />
          <el-button link type="primary" @click="selectAllFiltered">全选当前</el-button>
          <el-button link @click="clearAll">清空人选</el-button>
        </div>
      </div>

      <div v-if="notifyTypes.length" class="type-legend">
        <span v-for="t in notifyTypes" :key="t.key" class="legend-item">{{ t.label }}</span>
      </div>

      <div v-loading="loading" class="user-list">
        <div v-if="filteredUsers.length">
          <div v-for="u in filteredUsers" :key="u.user_id" class="user-row">
            <el-checkbox
              :model-value="isSelected(u.user_id)"
              @change="(v) => toggleUser(u, v)"
              class="user-check"
            >
              {{ u.full_name || u.username }}
              <span class="user-meta">{{ u.username }}{{ u.department ? ` · ${u.department}` : '' }}</span>
            </el-checkbox>
            <div v-if="isSelected(u.user_id)" class="type-checks">
              <el-checkbox
                v-for="t in notifyTypes"
                :key="t.key"
                :model-value="userHasType(u.user_id, t.key)"
                @change="(v) => toggleUserType(u.user_id, t.key, v)"
              >
                {{ t.label }}
              </el-checkbox>
            </div>
          </div>
        </div>
        <el-empty v-else-if="!loading" description="暂无可用用户" />
      </div>
    </div>

    <div class="section admin-section">
      <div class="section-title">管理员</div>
      <p class="admin-hint">
        一键将<strong>所有人</strong>当前未读/未完成的入库通知标为已知悉并完成任务，清除全员入库通知角标。
      </p>
      <el-button
        type="warning"
        plain
        :loading="acking"
        :disabled="loading"
        @click="ackAllUnread"
      >
        一键消除全部入库通知角标
      </el-button>
    </div>

    <template #footer>
      <span class="footer-hint">已选 {{ selectedCount }} 人</span>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  ackAllInboundNotifyUnread,
  getInboundNotifyRecipients,
  saveInboundNotifyRecipients
} from '@/api/taskEvents'

const visible = defineModel({ type: Boolean, default: false })

const loading = ref(false)
const saving = ref(false)
const acking = ref(false)
const filterKeyword = ref('')
const users = ref([])
const notifyTypes = ref([])
const defaultTypeKeys = ref([])
/** user_id -> enabled_type_keys[] ；有 key 即表示已选为接收人 */
const prefsByUser = reactive({})

const filteredUsers = computed(() => {
  const kw = filterKeyword.value.trim().toLowerCase()
  if (!kw) return users.value
  return users.value.filter((u) =>
    [u.full_name, u.username, u.department].some((x) =>
      String(x || '').toLowerCase().includes(kw)
    )
  )
})

const selectedCount = computed(() => Object.keys(prefsByUser).length)

function isSelected(userId) {
  return Object.prototype.hasOwnProperty.call(prefsByUser, String(userId))
}

function userHasType(userId, typeKey) {
  const keys = prefsByUser[String(userId)]
  return Array.isArray(keys) && keys.includes(typeKey)
}

function toggleUser(u, checked) {
  const id = String(u.user_id)
  if (checked) {
    const seed =
      (Array.isArray(u.enabled_type_keys) && u.enabled_type_keys.length
        ? u.enabled_type_keys
        : null) ||
      defaultTypeKeys.value ||
      notifyTypes.value.filter((t) => t.default).map((t) => t.key)
    prefsByUser[id] = [...new Set(seed.map(String))]
  } else {
    delete prefsByUser[id]
  }
}

function toggleUserType(userId, typeKey, checked) {
  const id = String(userId)
  if (!isSelected(id)) return
  const set = new Set(prefsByUser[id] || [])
  if (checked) set.add(typeKey)
  else set.delete(typeKey)
  prefsByUser[id] = [...set]
}

function selectAllFiltered() {
  for (const u of filteredUsers.value) {
    if (!isSelected(u.user_id)) toggleUser(u, true)
  }
}

function clearAll() {
  for (const k of Object.keys(prefsByUser)) delete prefsByUser[k]
}

async function onOpen() {
  loading.value = true
  clearAll()
  try {
    const res = await getInboundNotifyRecipients()
    const data = res?.data || {}
    users.value = data.users || []
    notifyTypes.value = data.notify_types || [
      { key: 'purchase_warehouse', label: '采购·入我方仓库', default: true },
      { key: 'purchase_transit', label: '采购·中转直发厂', default: true },
      { key: 'processing_warehouse', label: '加工·回仓', default: true },
      { key: 'processing_transit', label: '加工·中转', default: true },
      { key: 'opening', label: '期初入库', default: false },
      { key: 'overflow', label: '报溢入库', default: false }
    ]
    defaultTypeKeys.value = (
      data.default_enabled_type_keys ||
      notifyTypes.value.filter((t) => t.default).map((t) => t.key)
    ).map(String)

    for (const u of users.value) {
      if (!u.selected) continue
      const keys = Array.isArray(u.enabled_type_keys) ? u.enabled_type_keys.map(String) : []
      prefsByUser[String(u.user_id)] = keys.length ? keys : [...defaultTypeKeys.value]
    }
  } catch (e) {
    users.value = []
    ElMessage.error(e?.response?.data?.message || e?.message || '加载入库通知设置失败')
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  try {
    const recipients = Object.entries(prefsByUser)
      .map(([uid, keys]) => ({
        user_id: Number(uid),
        enabled_type_keys: Array.isArray(keys) ? keys : []
      }))
      .filter((r) => r.user_id > 0 && r.enabled_type_keys.length > 0)

    const res = await saveInboundNotifyRecipients({ recipients })
    if (res?.success !== false) {
      ElMessage.success(res?.message || '已保存')
      visible.value = false
    } else {
      ElMessage.error(res?.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function ackAllUnread() {
  try {
    await ElMessageBox.confirm(
      '将把所有人未读/未完成的入库通知全部标为已知悉并完成任务，清除全员角标。此操作不可撤销，是否继续？',
      '一键消除入库通知角标',
      { type: 'warning', confirmButtonText: '确认消除', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  acking.value = true
  try {
    const res = await ackAllInboundNotifyUnread()
    if (res?.success !== false) {
      ElMessage.success(res?.message || '已消除')
    } else {
      ElMessage.error(res?.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '操作失败')
  } finally {
    acking.value = false
  }
}
</script>

<style scoped>
.tip {
  margin-bottom: 14px;
}
.section {
  margin-top: 8px;
}
.section-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.type-legend {
  display: none;
}
.user-list {
  max-height: 420px;
  overflow: auto;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  padding: 8px 12px;
}
.user-row {
  padding: 8px 0;
  border-bottom: 1px solid var(--el-border-color-extra-light);
}
.user-row:last-child {
  border-bottom: none;
}
.user-check {
  display: block;
  width: 100%;
}
.user-meta {
  color: var(--el-text-color-secondary);
  font-size: 12px;
  margin-left: 6px;
}
.type-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 6px 0 0 22px;
}
.admin-section {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed var(--el-border-color-lighter);
}
.admin-hint {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}
.footer-hint {
  float: left;
  color: var(--el-text-color-secondary);
  font-size: 13px;
  line-height: 32px;
}
</style>
